import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import type { PointOptionsObject } from "highcharts";

import type {
    MarketResearchDemandAnalysisSearchTrendsListData,
    MarketResearchDemandAnalysisCommonModelsChangeValue,
} from "@similarweb/pro-api-client";

import {
    type KeywordListEntity,
    TOTAL_KEYWORD_LIST_ID,
    useKeywordLists,
    createKeywordListEntity,
} from "app/modules/demand-analysis/keyword-lists";

import { useKeywordListsNodeAPIQueryParams } from "../../hooks/useKeywordListsNodeAPIQueryParams";
import { queryOptionsBuilder } from "../../utils/queryOptionsBuilder";

interface Volume {
    change: number;
    value: number;
}

type BaseKeywordGroup = Pick<KeywordListEntity, "id" | "name">;

export interface KeywordGroupComparisonLegend extends BaseKeywordGroup, Volume {}

interface ColumnChartSeries {
    name: string;
    data: PointOptionsObject[];
    isPreviousPeriod: boolean;
    period: [string | undefined, string | undefined];
}

interface ColumnChartTooltipDataRecord {
    currentVolume: number;
    previousPeriodVolume?: number;
    change: number;
    monthToDateStartDate?: string;
}

type ColumnChartTooltipData = Record<string, ColumnChartTooltipDataRecord>;

interface LineChartSeries {
    id: string;
    name: string;
    isPeriodOverPeriod: boolean;
    data: PointOptionsObject[];
    monthToDateIndex: number;
}

export interface LineChartTooltipRecord {
    keywordList: {
        id: string;
        name: string;
        color?: string;
    };
    volume: number;
    comparedVolume?: number;
    monthlyVolumeChange?: MarketResearchDemandAnalysisCommonModelsChangeValue;
    periodVolumeChange?: MarketResearchDemandAnalysisCommonModelsChangeValue;
}

type Date = string;

interface LineChartTooltipRecordWithVolume {
    records: LineChartTooltipRecord[];
    totalVolume: number;
    comparedDate?: Date;
    isMonthToDate: boolean;
}

type LineChartTooltipData = Record<Date, LineChartTooltipRecordWithVolume>;

export interface KeywordListData {
    legendItems: KeywordGroupComparisonLegend[];
    legendTotal: KeywordGroupComparisonLegend;
    columnChartData: ColumnChartSeries[] | undefined;
    columnChartTooltipData: ColumnChartTooltipData | undefined;
    lineChartData: LineChartSeries[] | undefined;
    lineChartTooltipData: LineChartTooltipData | undefined;
    lineChartCategories: string[];
}

const virtualTotalKeywordGroup: BaseKeywordGroup = {
    id: TOTAL_KEYWORD_LIST_ID,
    name: TOTAL_KEYWORD_LIST_ID,
};

const buildKeywordGroupComparisonLegend = (
    keyword: BaseKeywordGroup,
    valueChange?: Volume,
): KeywordGroupComparisonLegend => {
    return {
        id: keyword.id,
        name: keyword.name,
        value: valueChange?.value ?? 0,
        change: valueChange?.change ?? 0,
    };
};

const buildLegendItems = (result: MarketResearchDemandAnalysisSearchTrendsListData): KeywordGroupComparisonLegend[] => {
    if (!result.children) {
        return [];
    }

    return result.children
        .map((child) => {
            const keywordListEntity = createKeywordListEntity(child);

            if (!keywordListEntity) {
                return undefined;
            }

            return buildKeywordGroupComparisonLegend(keywordListEntity, {
                value: child.currentPeriod.volume,
                change: child.currentPeriod.periodVolumeChange?.change ?? 0,
            });
        })
        .filter((legend) => legend !== undefined);
};

const buildColumnChartTooltipDataRecord = (
    id: string | symbol,
    currentVolume: number,
    change: number,
    previousPeriodVolume?: number,
    monthToDateStartDate?: string,
): [string | symbol, ColumnChartTooltipDataRecord] => {
    const record: ColumnChartTooltipDataRecord = {
        currentVolume,
        change,
        monthToDateStartDate,
    };
    if (previousPeriodVolume !== undefined) {
        record.previousPeriodVolume = previousPeriodVolume;
    }
    return [id, record];
};

const buildColumnChartData = (result: MarketResearchDemandAnalysisSearchTrendsListData): ColumnChartSeries[] => {
    if (!result.children) {
        return [];
    }

    const entries: ColumnChartSeries[] = [];
    const currentPeriodColumnChartSeriesData: PointOptionsObject[] = [];
    const previousPeriodColumnChartSeriesData: PointOptionsObject[] = [];

    for (const item of result.children) {
        const keywordListEntity = createKeywordListEntity(item);

        if (keywordListEntity) {
            currentPeriodColumnChartSeriesData.push({
                id: keywordListEntity.id,
                name: keywordListEntity.name,
                y: item.currentPeriod.volume,
            });
            if ("previousPeriod" in item && item.previousPeriod) {
                previousPeriodColumnChartSeriesData.push({
                    id: keywordListEntity.id,
                    name: keywordListEntity.name,
                    y: item.previousPeriod.volume,
                });
            }
        }
    }

    if (previousPeriodColumnChartSeriesData.length > 0) {
        const totalColumnChartSeriesData: PointOptionsObject = {
            id: TOTAL_KEYWORD_LIST_ID,
            name: virtualTotalKeywordGroup.name,
            y: result.previousPeriod?.volume ?? 0,
        };
        previousPeriodColumnChartSeriesData.push(totalColumnChartSeriesData);
        entries.push({
            name: "Compare Period",
            data: previousPeriodColumnChartSeriesData,
            isPreviousPeriod: true,
            period: [result.trend.at(0)?.previousPeriod?.date, result.trend.at(-1)?.previousPeriod?.date],
        });
    }

    if (currentPeriodColumnChartSeriesData.length > 0) {
        const totalColumnChartSeriesData: PointOptionsObject = {
            id: TOTAL_KEYWORD_LIST_ID,
            name: virtualTotalKeywordGroup.name,
            y: result.currentPeriod.volume,
        };
        currentPeriodColumnChartSeriesData.push(totalColumnChartSeriesData);
        entries.push({
            name: "Current Period",
            data: currentPeriodColumnChartSeriesData,
            isPreviousPeriod: false,
            period: [result.trend.at(0)?.currentPeriod.date, result.trend.at(-1)?.currentPeriod.date],
        });
    }

    return entries;
};

const buildColumnChartTooltipData = (
    result: MarketResearchDemandAnalysisSearchTrendsListData,
): ColumnChartTooltipData => {
    if (!result.children) {
        return {};
    }

    const entries = Object.fromEntries(
        result.children
            .map((item) => {
                const keywordListEntity = createKeywordListEntity(item);

                if (!keywordListEntity) {
                    return undefined;
                }

                return buildColumnChartTooltipDataRecord(
                    keywordListEntity.id,
                    item.currentPeriod.volume,
                    item.currentPeriod.periodVolumeChange?.change ?? 0,
                    item.previousPeriod?.volume,
                    item.trend.find(({ currentPeriod }) => currentPeriod.isMonthToDate)?.currentPeriod.date,
                );
            })
            .filter((record) => record !== undefined),
    );
    const [totalRecordKey, totalRecord] = buildColumnChartTooltipDataRecord(
        TOTAL_KEYWORD_LIST_ID,
        result.currentPeriod.volume,
        result.currentPeriod.periodVolumeChange?.change ?? 0,
        result.previousPeriod?.volume,
        result.trend.find(({ currentPeriod }) => currentPeriod.isMonthToDate)?.currentPeriod.date,
    );

    return { ...entries, [totalRecordKey]: totalRecord };
};

const buildLineChartData = (result: MarketResearchDemandAnalysisSearchTrendsListData): LineChartSeries[] => {
    if (!result.children) {
        return [];
    }

    const currentPeriodLineChartSeriesData: LineChartSeries[] = [];
    const previousPeriodLineChartSeriesData: LineChartSeries[] = [];

    for (const item of result.children) {
        const keywordListEntity = createKeywordListEntity(item);

        if (keywordListEntity) {
            currentPeriodLineChartSeriesData.push({
                id: keywordListEntity.id,
                name: keywordListEntity.name,
                isPeriodOverPeriod: false,
                data: item.trend.map(({ currentPeriod }, index) => ({
                    x: index,
                    y: currentPeriod.volume,
                    isMonthToDate: !!currentPeriod.isMonthToDate,
                })),
                monthToDateIndex: item.trend.findIndex(({ currentPeriod }) => currentPeriod.isMonthToDate),
            });

            if ("previousPeriod" in item) {
                previousPeriodLineChartSeriesData.push({
                    id: keywordListEntity.id,
                    name: keywordListEntity.name,
                    isPeriodOverPeriod: true,
                    data: item.trend
                        .map(({ previousPeriod }, index) =>
                            previousPeriod ? { x: index, y: previousPeriod.volume } : undefined,
                        )
                        .filter((entry) => entry !== undefined),
                    monthToDateIndex: -1,
                });
            }
        }
    }

    if (currentPeriodLineChartSeriesData.length > 0) {
        currentPeriodLineChartSeriesData.push({
            id: TOTAL_KEYWORD_LIST_ID,
            name: virtualTotalKeywordGroup.name,
            isPeriodOverPeriod: false,
            data: result.trend.map(({ currentPeriod }, index) => ({
                x: index,
                y: currentPeriod.volume,
                isMonthToDate: !!currentPeriod.isMonthToDate,
            })),
            monthToDateIndex: result.trend.findIndex(({ currentPeriod }) => currentPeriod.isMonthToDate),
        });
    }

    if (previousPeriodLineChartSeriesData.length > 0) {
        previousPeriodLineChartSeriesData.push({
            id: TOTAL_KEYWORD_LIST_ID,
            name: virtualTotalKeywordGroup.name,
            isPeriodOverPeriod: true,
            data: result.trend
                .map(({ previousPeriod }, index) =>
                    previousPeriod ? { x: index, y: previousPeriod.volume } : undefined,
                )
                .filter((entry) => entry !== undefined),
            monthToDateIndex: -1,
        });
    }

    return [...currentPeriodLineChartSeriesData, ...previousPeriodLineChartSeriesData];
};

const buildLineChartCategories = (result: MarketResearchDemandAnalysisSearchTrendsListData): string[] => {
    return result.trend.map(({ currentPeriod }) => currentPeriod.date);
};

const buildLineChartTooltipData = (result: MarketResearchDemandAnalysisSearchTrendsListData): LineChartTooltipData => {
    const lineChartTooltipData = Object.fromEntries(
        result.trend?.map(({ currentPeriod, previousPeriod }, index) => {
            const { date, volume: totalVolume } = currentPeriod;
            let isMonthToDate = false;

            const records: LineChartTooltipRecord[] =
                result.children
                    ?.map((child) => {
                        const keywordListEntity = createKeywordListEntity(child);
                        const dataPoint = child.trend[index];

                        if (!keywordListEntity || !dataPoint) {
                            return undefined;
                        }

                        if (dataPoint.currentPeriod.isMonthToDate) {
                            isMonthToDate = true;
                        }

                        const currentValue = dataPoint.currentPeriod.volume ?? 0;
                        const comparedValue =
                            "previousPeriod" in dataPoint && dataPoint.previousPeriod
                                ? dataPoint.previousPeriod.volume
                                : undefined;

                        return {
                            keywordList: {
                                id: keywordListEntity.id,
                                name: keywordListEntity.name,
                            },
                            volume: currentValue,
                            comparedVolume: comparedValue,
                            monthlyVolumeChange: dataPoint.currentPeriod.monthlyVolumeChange,
                            periodVolumeChange: dataPoint.currentPeriod.periodVolumeChange,
                        };
                    })
                    .filter((record) => record !== undefined) ?? [];
            const comparedDate = previousPeriod?.date;

            return [date, { records, totalVolume, comparedDate, isMonthToDate }];
        }) ?? [],
    );

    return lineChartTooltipData;
};

const processSearchTrendsKeywordsData = (result: MarketResearchDemandAnalysisSearchTrendsListData): KeywordListData => {
    return {
        legendItems: buildLegendItems(result),
        legendTotal: buildKeywordGroupComparisonLegend(virtualTotalKeywordGroup, {
            value: result.currentPeriod.volume,
            change: result.currentPeriod.periodVolumeChange?.change ?? 0,
        }),
        columnChartData: buildColumnChartData(result),
        columnChartTooltipData: buildColumnChartTooltipData(result),
        lineChartData: buildLineChartData(result),
        lineChartTooltipData: buildLineChartTooltipData(result),
        lineChartCategories: buildLineChartCategories(result),
    };
};

export function useKeywordListData({ isMonthToDate }: { isMonthToDate?: boolean } = { isMonthToDate: false }) {
    const { keywordLists } = useKeywordLists();
    const queryParams = useKeywordListsNodeAPIQueryParams({ isMonthToDate });
    const queryOptions = useMemo(
        () =>
            queryOptionsBuilder("DemandSearchTrends", "marketResearchDemandAnalysisSearchTrendsList")
                .setSelect(processSearchTrendsKeywordsData)
                .setArgs(queryParams)
                .build(),
        [queryParams],
    );
    const queryResult = useQuery(queryOptions);
    const initialData: KeywordListData = {
        legendItems: keywordLists.map((keyword) => buildKeywordGroupComparisonLegend(keyword)),
        legendTotal: buildKeywordGroupComparisonLegend(virtualTotalKeywordGroup),
        columnChartData: undefined,
        columnChartTooltipData: undefined,
        lineChartData: undefined,
        lineChartTooltipData: undefined,
        lineChartCategories: [],
    };

    return {
        isLoading: queryResult.isLoading,
        isError: queryResult.isError,
        isSuccess: queryResult.isSuccess,
        data: queryResult.isSuccess ? queryResult.data : initialData,
    };
}
